/**
 * Document-lifetime installation of Core's agent surface.
 *
 * WHY THIS IS NOT A REACT EFFECT
 *
 * `registerTool` REJECTS with `InvalidStateError` / "Duplicate tool name" when
 * a name registers twice (model_context.cc, checked before the abort-signal
 * check). A rejection is not a throw, so no error boundary can see it. React
 * StrictMode double-invokes effects and Vite replaces modules on HMR, so any
 * effect-shaped registration will duplicate in development. The fix is not to
 * catch the duplicate, it is to make a second registration unreachable.
 * Registration is therefore a module-load event with a document-scoped guard.
 *
 * WHAT THIS MODULE DELIBERATELY DOES NOT DO
 *
 * No zod-to-JSON-Schema conversion, no argument validation, no tenant
 * resolution, no confirmation, no output encoding. Those belong to the runtime
 * and arrive through `InstallDeps.buildBody`. This module owns the lifecycle,
 * the guard, the abort controller, and the annotations.
 */

import { emjaeExposedToolNames } from './contract';
import type { AnyCommand, CommandRecord, Consequence } from './contract';
import { resolveModelContext } from './modelContext';

/* -------------------------------------------------------------------------- */
/* The native surface                                                          */
/* -------------------------------------------------------------------------- */

/*
 * The `WebMCP.*` types are ambient, declared in `./webmcp.d.ts`, and written
 * against SHIPPING Chrome rather than against the editor's draft or against
 * `webmcp-types`. This module used to carry its own structurally-identical
 * placeholders; they are gone, so there is exactly one description of the
 * browser API and it cannot drift from the one the runtime codes against.
 *
 */

/**
 * Annotations as THIS module produces them.
 *
 * Both hints are required here while `WebMCP.ToolAnnotations` marks them
 * optional, because Chromium hard-`CHECK`s every member once an `annotations`
 * dictionary is passed at all. Deriving a partial one would be a crash, so the
 * type makes a partial one unconstructable.
 *
 * `consequentialHint` is deliberately not included. It ships in Chromium's IDL
 * but appears in no spec, explainer or doc page, so we take the IDL default
 * rather than guess at semantics.
 */
type DerivedAnnotations = Required<
  Pick<WebMCP.ToolAnnotations, 'readOnlyHint' | 'untrustedContentHint'>
>;

/** A tool as this module assembles it, with annotations proven present. */
type SurfaceTool = Omit<WebMCP.ModelContextTool, 'annotations'> & {
  readonly annotations: DerivedAnnotations;
};

/* -------------------------------------------------------------------------- */
/* Public shapes                                                               */
/* -------------------------------------------------------------------------- */

/**
 * What this module installs: commands by name, already proven duplicate-free.
 *
 * `asCatalog` returns a `CatalogBuild`, this record plus the duplicate names it
 * had to drop, and `mount.ts` is what refuses to install when that list is not
 * empty. This module therefore never sees a catalog it has to reject for a
 * reason its caller already knows about.
 */
export type Catalog = CommandRecord;

/** The two members of a tool this module cannot derive from a `Command`. */
export interface ToolBody {
  /** JSON Schema draft 2020-12. Advisory to Chrome, which validates nothing. */
  readonly inputSchema?: WebMCP.JsonSchema;
  readonly execute: WebMCP.ToolExecuteCallback;
}

/** Field-bag logging, shaped to match `@/lib/logger` so `log` can be passed directly. */
export interface SurfaceReporter {
  info(message: string, fields?: Record<string, unknown>): void;
  warn(message: string, fields?: Record<string, unknown>): void;
  error(message: string, fields?: Record<string, unknown>): void;
}

export interface InstallDeps {
  /**
   * Wraps one command into its executable body. Deliberately cannot supply
   * `name`, `description`, or `annotations`. Those are derived here so that no
   * future author can hand-author a `readOnlyHint` that contradicts the
   * command's declared `consequence`.
   */
  buildBody(command: AnyCommand): ToolBody;
  report?: SurfaceReporter;
}

export type UnavailableReason =
  /** No `document`. Server render or a non-DOM test environment. */
  | 'no-document'
  /** `[SecureContext]` gates the attribute out of existence on http origins. */
  | 'insecure-context'
  /** No Chrome WebMCP, flag off, or the origin-trial token is missing. */
  | 'api-absent'
  /** The slot holds a record this build cannot interpret. Never stomp it. */
  | 'slot-occupied-by-unknown';

export type FailurePhase = 'build' | 'precondition' | 'register' | 'aborted';

export interface ToolFailure {
  readonly name: string;
  readonly errorName: string;
  readonly message: string;
}

export type InstallOutcome =
  | {
      readonly kind: 'installed';
      readonly toolCount: number;
      readonly fingerprint: string;
      /**
       * The subset of `toolCount` that opted in to Emjae, sorted. Reported from
       * here rather than re-derived from the catalog so the Eve client filters
       * against what actually registered in this document.
       */
      readonly emjaeExposed: readonly string[];
      /** True when this call found a live, fingerprint-identical installation. */
      readonly reused: boolean;
    }
  | {
      readonly kind: 'unavailable';
      readonly because: UnavailableReason;
      readonly detail?: string;
    }
  | {
      /** A live installation exists under a different catalog. Needs a reload. */
      readonly kind: 'stale-catalog';
      readonly installedFingerprint: string;
      readonly requestedFingerprint: string;
    }
  | {
      /** Someone else in this document already owns one of our names. */
      readonly kind: 'foreign-collision';
      readonly names: readonly string[];
      readonly incumbents: readonly WebMCP.RegisteredTool[];
    }
  | {
      readonly kind: 'failed';
      readonly phase: FailurePhase;
      readonly failures: readonly ToolFailure[];
    };

/* -------------------------------------------------------------------------- */
/* The guard slot                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A module-level `let installed = false` is the specific bug this avoids. Vite
 * HMR evaluates a NEW module instance with FRESH module-level state while the
 * old instance's registrations are still live on the document, so the boolean
 * reads false and we register a second time into an occupied tool map.
 *
 * `Symbol.for` resolves through the cross-realm registry keyed by this string,
 * so every module instance in this realm gets the identical symbol and reads
 * the identical `globalThis` property. The property outlives module identity
 * and dies only with the document, which is exactly the registration's scope.
 *
 * The version lives in the key. If the record shape ever changes, bump to v2 so
 * an old record is not found rather than misread.
 */
const SLOT = Symbol.for('agileco.core.agent-surface.install.v1');

interface Installation {
  readonly slotVersion: 1;
  readonly fingerprint: string;
  /** Folded into `fingerprint`, so it cannot disagree with the installed catalog. */
  readonly emjaeExposed: readonly string[];
  /** Null on the `unavailable` path, where there is nothing to unregister. */
  readonly controller: AbortController | null;
  /** Settles, never rejects. Storing a rejecting promise here would be a leak. */
  readonly outcome: Promise<InstallOutcome>;
  readonly detachPagehide: () => void;
}

type SlotHost = Record<symbol, unknown>;

function slotHost(): SlotHost {
  return globalThis as unknown as SlotHost;
}

function isInstallation(value: unknown): value is Installation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Installation>;
  return (
    candidate.slotVersion === 1 &&
    typeof candidate.fingerprint === 'string' &&
    typeof (candidate.outcome as { then?: unknown } | undefined)?.then === 'function'
  );
}

function readSlot(): { present: boolean; record: Installation | null } {
  const raw = slotHost()[SLOT];
  if (raw === undefined) return { present: false, record: null };
  return { present: true, record: isInstallation(raw) ? raw : null };
}

function claimSlot(record: Installation): void {
  slotHost()[SLOT] = record;
}

/** Releases only our own record, so a newer build's claim is never stomped. */
function releaseSlot(record: Installation): void {
  const host = slotHost();
  if (host[SLOT] === record) delete host[SLOT];
}

/* -------------------------------------------------------------------------- */
/* Annotations, derived and never hand-authored                                */
/* -------------------------------------------------------------------------- */

/**
 * `consequence` is the single input to the safety policy, so it is the single
 * input here. Written as an exhaustive switch rather than a lookup table so the
 * reason for each arm sits next to the arm.
 */
function annotationsFor(consequence: Consequence): DerivedAnnotations {
  switch (consequence) {
    case 'reads':
      // Read-only to Chrome. Still untrusted output, because a receipt can echo
      // Brand Bible prose, which is operator- and model-authored text arriving
      // from the database. Marking it trusted would be a lie.
      return { readOnlyHint: true, untrustedContentHint: true };
    case 'writes-config':
      return { readOnlyHint: false, untrustedContentHint: true };
    case 'writes-generation-input':
      return { readOnlyHint: false, untrustedContentHint: true };
    case 'spends-budget':
      // Not read-only: it enqueues a generation run that costs real money and
      // is not undone by reverting the row.
      return { readOnlyHint: false, untrustedContentHint: true };
    case 'writes-live-site':
      // Not read-only, and the output can echo article prose destined for a
      // client's public site, so it is untrusted for the same reason a read is.
      return { readOnlyHint: false, untrustedContentHint: true };
    case 'destroys-data':
      // Reserved for wave two. Chrome's `destructiveHint` is deliberately NOT
      // used: it is one of the stale pre-Chrome-150 annotations, so the safety
      // decision stays ours rather than being delegated to a hint the browser
      // no longer reads.
      return { readOnlyHint: false, untrustedContentHint: true };
    default: {
      // A new `Consequence` member must decide its own hints here. Failing is
      // the point; a silent default would mislabel a writer as read-only.
      const unreachable: never = consequence;
      throw new Error(`Unhandled consequence: ${String(unreachable)}`);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Fingerprint                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Key-order-stable JSON. A schema generator that emits the same schema with
 * different key insertion order must not read as a changed catalog, because
 * that would demand a full reload on every unrelated HMR cycle.
 */
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, member]) => member !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, member]) => `${JSON.stringify(key)}:${canonical(member)}`);
  return `{${entries.join(',')}}`;
}

/** FNV-1a 32-bit. Not a security boundary, only a change detector. */
function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Fingerprints everything an installation exposes: what Chrome would see,
 * including the generated input schema so a change confined to a zod schema is
 * still detected, plus the Emjae opt-in list, which Chrome never sees but which
 * changes who the surface is exposed to. Leaving the opt-in out would let a
 * newly exposed tool land on a fingerprint-identical catalog and be reused
 * away. Count and length are folded in so an accidental 32-bit collision is
 * harmless in practice.
 */
function fingerprintOf(tools: readonly SurfaceTool[], emjaeExposed: readonly string[]): string {
  const body = tools
    .slice()
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    .map((tool) =>
      canonical({
        name: tool.name,
        description: tool.description,
        inputSchema: tool.inputSchema ?? null,
        annotations: tool.annotations,
      }),
    )
    .join('\n');
  const scoped = `${body}\nemjae:${emjaeExposed.join(',')}`;
  return `${tools.length}.${scoped.length.toString(16)}.${fnv1a(scoped)}`;
}

/* -------------------------------------------------------------------------- */
/* Preconditions Chrome actually enforces                                      */
/* -------------------------------------------------------------------------- */

/** Mirrors `IsValidToolName` in model_context.cc. 128 is the enforced cap. */
const VALID_TOOL_NAME = /^[A-Za-z0-9_.-]{1,128}$/;

function describeError(error: unknown): { errorName: string; message: string } {
  if (error instanceof Error) return { errorName: error.name, message: error.message };
  return { errorName: 'UnknownError', message: String(error) };
}

/**
 * Converts four classes of asynchronous `registerTool` rejection into a
 * synchronous, precise diagnostic, and does so before ANY tool is registered.
 * Failing at zero registered is strictly better than aborting at six.
 */
function preconditionFailures(tools: readonly SurfaceTool[]): ToolFailure[] {
  const failures: ToolFailure[] = [];
  const seen = new Set<string>();

  for (const tool of tools) {
    if (seen.has(tool.name)) {
      // `asCatalog` keys by `command.name`, so a well-formed catalog cannot
      // reach here. A hand-built record can, and Chrome would answer with the
      // same async rejection this module exists to make impossible.
      failures.push({
        name: tool.name,
        errorName: 'InvalidStateError',
        message: 'Duplicate tool name within our own catalog',
      });
    }
    seen.add(tool.name);

    if (!VALID_TOOL_NAME.test(tool.name)) {
      failures.push({
        name: tool.name,
        errorName: 'InvalidStateError',
        message: 'Invalid tool name: must be 1-128 chars of [A-Za-z0-9_.-]',
      });
    }

    if (tool.description.trim() === '') {
      failures.push({
        name: tool.name,
        errorName: 'InvalidStateError',
        message: 'Description is required',
      });
    }

    if (tool.inputSchema !== undefined) {
      try {
        if (JSON.stringify(tool.inputSchema) === undefined) {
          failures.push({
            name: tool.name,
            errorName: 'TypeError',
            message: 'invalid input schema: toJSON() returns undefined',
          });
        }
      } catch (error) {
        // Circular reference, or a throwing toJSON. Chrome rethrows the raw
        // TypeError out of JSON.stringify; we catch it one layer earlier.
        failures.push({ name: tool.name, ...describeError(error) });
      }
    }
  }
  return failures;
}

/** Chrome's message is not spec-normative, so match tolerantly around the name. */
function isDuplicateName(error: unknown): boolean {
  return (
    error instanceof Error &&
    error.name === 'InvalidStateError' &&
    /duplicate tool name/i.test(error.message)
  );
}

/* -------------------------------------------------------------------------- */
/* Entry point                                                                 */
/* -------------------------------------------------------------------------- */

const NO_REPORTER: SurfaceReporter = {
  info: () => undefined,
  warn: () => undefined,
  error: () => undefined,
};

/**
 * CALL THIS AT MODULE SCOPE. Not from a `useEffect`, not from a component, not
 * from a route guard. The lifetime being managed is the document's, and React
 * has no lifecycle that matches it.
 *
 * The returned promise NEVER rejects. Every failure is an `InstallOutcome`
 * variant. This is not politeness, it is the requirement. Chrome's own
 * published React hook wraps an async `registerTool` in a synchronous
 * try/catch, reports success, and emits an unhandled rejection anyway. A
 * floating rejection from this module would trip `window.onunhandledrejection`
 * in `main.tsx` and page Sentry for a duplicate that is, by construction,
 * benign.
 *
 * There is no `await` anywhere before the slot is claimed. This function is not
 * `async`, so its entire detect-guard-claim sequence runs in one uninterrupted
 * synchronous turn and two callers cannot both pass the guard.
 */
export function installAgentSurface(
  catalog: Catalog,
  deps: InstallDeps,
): Promise<InstallOutcome> {
  const report = deps.report ?? NO_REPORTER;

  /* ---- 1. Feature detection. No kill switch, no fallback, no polyfill. ---- */

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    report.info('[agent-surface] not installed', { because: 'no-document' });
    return Promise.resolve({ kind: 'unavailable', because: 'no-document' });
  }

  const existingSlot = readSlot();
  if (existingSlot.present && existingSlot.record === null) {
    // Our well-known slot holds something we cannot interpret: a future build
    // of this module, or a foreign squatter. Adopting or stomping it is worse
    // than standing down.
    report.error('[agent-surface] guard slot holds an unrecognized record; not installing');
    return Promise.resolve({ kind: 'unavailable', because: 'slot-occupied-by-unknown' });
  }

  const native = resolveModelContext();
  if (!native || typeof native.registerTool !== 'function') {
    if (existingSlot.record) return existingSlot.record.outcome;
    const because: UnavailableReason = window.isSecureContext ? 'api-absent' : 'insecure-context';
    const detail =
      because === 'insecure-context'
        ? 'WebMCP model context is [SecureContext]; serve over https or localhost'
        : 'no WebMCP in this browser, or chrome://flags/#enable-webmcp-testing is off, or the origin-trial token is absent';
    report.info('[agent-surface] native WebMCP unavailable; agent surface not installed', {
      because,
      detail,
    });
    return commitUnavailable(because, detail).outcome;
  }

  /* ---- 2. Build every tool. Synchronous, so the fingerprint is available. -- */

  const commands = Object.values(catalog);
  const tools: SurfaceTool[] = [];
  const buildFailures: ToolFailure[] = [];

  for (const command of commands) {
    try {
      const body = deps.buildBody(command);
      tools.push({
        name: command.name,
        description: command.description,
        ...(body.inputSchema === undefined ? {} : { inputSchema: body.inputSchema }),
        execute: body.execute,
        annotations: annotationsFor(command.consequence),
      });
    } catch (error) {
      buildFailures.push({ name: command.name, ...describeError(error) });
    }
  }

  if (buildFailures.length > 0) {
    // Nothing was registered, so there is nothing to unwind and the slot is
    // untouched. A later corrected HMR pass installs cleanly.
    report.error('[agent-surface] catalog failed to build; nothing registered', {
      failures: buildFailures,
    });
    return Promise.resolve({ kind: 'failed', phase: 'build', failures: buildFailures });
  }

  const emjaeExposed = emjaeExposedToolNames(catalog);
  const fingerprint = fingerprintOf(tools, emjaeExposed);

  /* ---- 3. Guard. Identical catalog reuses; changed catalog needs a reload. - */

  const previous = existingSlot.record;
  if (previous) {
    if (previous.fingerprint === fingerprint) {
      report.info('[agent-surface] already installed for this document; reusing', {
        fingerprint,
        toolCount: tools.length,
      });
      return previous.outcome.then((outcome) =>
        outcome.kind === 'installed' ? { ...outcome, reused: true } : outcome,
      );
    }

    // A changed catalog cannot be hot-swapped, for two reasons not fixable
    // here. Abort-then-reregister leaves a window in which an agent enumerates
    // zero tools, and any in-flight execution is bound to the old module's
    // closures and would resolve against state the new module no longer owns.
    // Aborting on HMR is also exactly what the abort controller's document
    // scope forbids. So the old registration keeps serving and we say so.
    report.warn(
      '[agent-surface] catalog changed but a different catalog is already registered; RELOAD THE PAGE. The previously registered tools are still live and serving.',
      { installedFingerprint: previous.fingerprint, requestedFingerprint: fingerprint },
    );
    return Promise.resolve({
      kind: 'stale-catalog',
      installedFingerprint: previous.fingerprint,
      requestedFingerprint: fingerprint,
    });
  }

  /* ---- 4. Preconditions Chrome enforces, checked before touching Chrome. --- */

  const preFailures = preconditionFailures(tools);
  if (preFailures.length > 0) {
    report.error('[agent-surface] catalog violates Chrome tool preconditions; nothing registered', {
      failures: preFailures,
    });
    return Promise.resolve({ kind: 'failed', phase: 'precondition', failures: preFailures });
  }

  if (tools.length === 0) {
    // Not an error. During incremental build-out the catalog is legitimately
    // empty, and blocking here would wedge every other lane. Claim the slot so
    // the eventual non-empty catalog reads as a changed fingerprint.
    report.warn('[agent-surface] catalog is empty; the agent surface will expose no tools');
  }

  /* ---- 5. Claim the slot. Still synchronous, still one turn. -------------- */

  // One controller owns the whole catalog. It is aborted on genuine document
  // destruction and on a failed install, and on nothing else. Not on route
  // change, not on logout, not on unmount, not on HMR. Aborting unregisters
  // (there is no `unregisterTool`), and since Chrome 153 an abort does not
  // cancel in-flight executions, so a real teardown will not corrupt a call
  // that is already running.
  const controller = new AbortController();

  let resolveOutcome!: (outcome: InstallOutcome) => void;
  const outcome = new Promise<InstallOutcome>((resolve) => {
    resolveOutcome = resolve;
  });

  const onPagehide = (event: PageTransitionEvent): void => {
    // `persisted` true means bfcache: the document is frozen, not destroyed,
    // and will be restored with its registrations intact. Aborting here would
    // silently strip the surface from every back-navigation.
    if (event.persisted) return;
    controller.abort(new DOMException('Document destroyed', 'AbortError'));
    releaseSlot(record);
    detachPagehide();
    report.info('[agent-surface] document destroyed; agent surface unregistered');
  };
  const detachPagehide = (): void => {
    window.removeEventListener('pagehide', onPagehide);
  };

  const record: Installation = {
    slotVersion: 1,
    fingerprint,
    emjaeExposed,
    controller,
    outcome,
    detachPagehide,
  };

  claimSlot(record);
  // Attached before registration begins, so a teardown during a slow install
  // still unwinds. Deliberately not `{ once: true }`: a bfcache pagehide would
  // consume a once-listener and leave the real destruction unhandled.
  window.addEventListener('pagehide', onPagehide);

  /* ---- 6. Register. The first await lives strictly beyond this point. ----- */

  void register(native, tools, record, report).then(resolveOutcome, (error: unknown) => {
    // Unreachable by construction, `register` catches everything. Present so
    // that "the stored promise never rejects" stays true if that ever changes.
    const failure = { name: '*', ...describeError(error) };
    report.error('[agent-surface] installer threw unexpectedly', failure);
    resolveOutcome({ kind: 'failed', phase: 'register', failures: [failure] });
  });

  return outcome;
}

/* -------------------------------------------------------------------------- */
/* Registration                                                                */
/* -------------------------------------------------------------------------- */

async function register(
  native: WebMCP.ModelContext,
  tools: readonly SurfaceTool[],
  record: Installation,
  report: SurfaceReporter,
): Promise<InstallOutcome> {
  const signal = record.controller?.signal;

  // `allSettled`, not `all`. Every promise gets a handler, so no rejection can
  // float, and we learn about every failure rather than only the first.
  const results = await Promise.allSettled(
    tools.map((tool) => native.registerTool(tool, signal ? { signal } : {})),
  );

  const failures: ToolFailure[] = [];
  const duplicates: string[] = [];
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') return;
    failures.push({ name: tools[index].name, ...describeError(result.reason) });
    if (isDuplicateName(result.reason)) duplicates.push(tools[index].name);
  });

  if (failures.length === 0) {
    report.info('[agent-surface] installed', {
      toolCount: tools.length,
      fingerprint: record.fingerprint,
      emjaeExposed: record.emjaeExposed,
    });
    return {
      kind: 'installed',
      toolCount: tools.length,
      fingerprint: record.fingerprint,
      emjaeExposed: record.emjaeExposed,
      reused: false,
    };
  }

  /* ---- Fail closed. Never leave a partial catalog claiming success. ------- */

  // Read this BEFORE rolling back, because the rollback itself sets `aborted`
  // and would make the test tautological.
  const tornDownMidInstall = signal?.aborted === true;
  const registered = results.length - failures.length;

  // Aborting the shared controller unregisters every tool that DID register.
  // All `registerTool` calls were issued in one synchronous turn, so all of
  // them passed Chrome's already-aborted check before any rejection could be
  // observed, which means every successful registration carries this signal's
  // unregister algorithm and every one of them is torn down here.
  record.controller?.abort(new DOMException('Partial registration', 'AbortError'));
  record.detachPagehide();
  releaseSlot(record);

  if (tornDownMidInstall) {
    report.warn('[agent-surface] document torn down mid-install; nothing left registered', {
      failures,
    });
    return { kind: 'failed', phase: 'aborted', failures };
  }

  /* ---- Ours or theirs. --------------------------------------------------- */

  if (duplicates.length > 0) {
    // A duplicate is always same-document: Chromium checks this Document's own
    // `tool_map_`. Our slot was empty when this call began, so we had nothing
    // registered here, so the incumbent is not ours to abort. It is either a
    // foreign registrant (extension content script, tag-manager payload) or a
    // registration orphaned from a module generation whose slot was cleared
    // without its controller being aborted. Both are terminal for the same
    // reason: we hold no controller for those tools, so we cannot unregister
    // them, and we cannot verify what they do when an agent calls a name we
    // published. Adopting them is not an option. A full reload is the remedy.
    const incumbents = await incumbentsFor(native, duplicates);
    report.error(
      '[agent-surface] Core tool names are already owned by a registrant we do not control; agent surface NOT installed. Reload the page.',
      { names: duplicates, incumbents },
    );
    return { kind: 'foreign-collision', names: duplicates, incumbents };
  }

  report.error('[agent-surface] registration failed; rolled back the whole catalog', {
    failed: failures.length,
    rolledBack: registered,
    failures,
  });
  return { kind: 'failed', phase: 'register', failures };
}

/**
 * Evidence for a collision. `origin` cannot discriminate, because a
 * same-document squatter shares our origin, so the useful signal is the
 * incumbent's own title and description.
 */
async function incumbentsFor(
  native: WebMCP.ModelContext,
  names: readonly string[],
): Promise<readonly WebMCP.RegisteredTool[]> {
  try {
    const registered = await native.getTools();
    return registered.filter((tool) => names.includes(tool.name));
  } catch {
    // `getTools` rejects on the same document, agent-cluster and
    // permissions-policy conditions as `registerTool`. Losing the evidence must
    // not lose the verdict, and this rejection must not float.
    return [];
  }
}

/* -------------------------------------------------------------------------- */
/* Unavailable                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Records the absence in the slot so an HMR cycle re-probes silently instead of
 * re-logging. The API cannot appear later in the same document's life.
 */
function commitUnavailable(because: UnavailableReason, detail: string): Installation {
  const record: Installation = {
    slotVersion: 1,
    // No catalog is installed, so no catalog can be stale. The empty
    // fingerprint never equals a real one, and callers key on
    // `controller === null`.
    fingerprint: '',
    emjaeExposed: [],
    controller: null,
    outcome: Promise.resolve<InstallOutcome>({ kind: 'unavailable', because, detail }),
    detachPagehide: () => undefined,
  };
  claimSlot(record);
  return record;
}
